import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type * as MapLibre from 'maplibre-gl';
import {
  fetchExplore, fetchExploreCard, type ExploreCard, type ExploreFilters,
  type ExploreFrame, type ExploreResponse, type Viewport,
} from '../../services/exploreApi';
import { ListSheet } from './ListSheet';
import { MapSearch } from './MapSearch';
import { ViewFilters } from './ViewFilters';
import {
  loadEngine, STYLE_URLS, cartoTransformRequest, creditElement,
  ENGINE_MAX_ZOOM, ENGINE_MIN_ZOOM,
} from '../../utils/mapUtils';
import type { MapLibreNamespace } from '../../utils/mapUtils';
import { boundsForCamera, cameraForBounds, radiusBounds } from '../../utils/mercator';
import type { Box, LatLng, Size } from '../../utils/mercator';
import { clusterElement, pinElement } from './ExplorePin';
import { normalizeViewport, toEngineZoom, toServerZoom } from './viewport';
import './ExploreSurface.css';

/**
 * The Explore map and its list (spec §1.1, §1.5, §3).
 *
 * NOT a descendant of `WorldMap`: that is a passport visual with a fixed centre
 * and zoom which fetches nothing. They share a map library and nothing else.
 *
 * **The list is rendered BEFORE the map in the DOM and stays first visually**
 * (§1.1: "a split view — list left, map right"). Source order is what a screen
 * reader follows, so the guarantee lives in the markup; the stylesheet needs no
 * reordering trick, which is what a first version used and which contradicted
 * the spec's own layout.
 *
 * **The map is not `aria-hidden`.** It was, and that was wrong twice over: a
 * real map renders a focusable container, zoom controls and the
 * attribution link, so hiding it left ghost tab stops announced as nothing
 * (WCAG 4.1.2); and the pins inside carry carefully-spoken labels that could
 * never be spoken. The list carries everything the map does, so a reader may
 * use either — duplication is the cost of two views of one set, and it is
 * cheaper than a surface that is half-hidden.
 */
const DEBOUNCE_MS = 300;

/** THE STYLESHEET'S OWN BREAKPOINT, and it has to stay the stylesheet's own.
 *
 *  `ExploreSurface.css` neutralises every narrow-layout rule above 900px, and
 *  until slice C that was enough on its own: the narrow states changed how the
 *  panels were painted and nothing else, so at desktop width they simply had
 *  no effect. The peek is the first narrow state that changes WHAT IS
 *  RENDERED, so it needs the same condition in JavaScript — a rule the CSS
 *  cancels and the JSX does not is a one-card list column on a desktop screen
 *  (internal round 1, blocking 2). Two copies of one number, which is a cost;
 *  the alternative is a peek that survives a rotation. */
const NARROW_BREAKPOINT = '(max-width: 899.98px)';

/** The narrow media query, or `null` when this environment cannot answer.
 *
 *  Defensive about the RESULT and not only about the function: `setupTests.ts`
 *  installs `matchMedia` as a `jest.fn()` with an implementation, and
 *  `react-scripts` sets `resetMocks: true` — so in every test it is a function
 *  that returns `undefined`. Production reads `.matches` off whatever comes
 *  back; without this check that is a crash in the suite and nowhere else,
 *  which is the worst place for one to live. */
const narrowQuery = (): MediaQueryList | null => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return null;
  }
  const query = window.matchMedia(NARROW_BREAKPOINT);
  return query && typeof query.matches === 'boolean' ? query : null;
};

/** A card plus whether it was inside the viewport of the response it came in.
 *  `position` and `in_view_count` are both absolute within a snapshot, so the
 *  flag is decidable per card and survives being merged with another snapshot's
 *  cards — which a slice at a single count does not. */
export type PlacedCard = ExploreCard & { inView: boolean };

const placed = (r: ExploreResponse): PlacedCard[] =>
  r.cards.map((c) => ({ ...c, inView: c.position < r.in_view_count }));

/** §1.5's "deduping by slug across the seam". The reader keeps what they have
 *  scrolled to; only genuinely new slugs are appended. */
const merge = (current: PlacedCard[], incoming: PlacedCard[]): PlacedCard[] => {
  const seen = new Set(current.map((c) => c.slug));
  return [...current, ...incoming.filter((c) => !seen.has(c.slug))];
};

/** The frame, when it carries coordinates to open on. */
const framedOf = (frame: ExploreFrame | 'failed' | undefined): ExploreFrame | null =>
  (frame && frame !== 'failed' && frame.lat !== null && frame.lng !== null ? frame : null);

/** D14: a pane with no size (hidden, or not laid out yet) is measured as this. */
const FALLBACK_PANE: Size = { width: 600, height: 400 };

const paneSizeOf = (el: HTMLElement | null): Size => {
  const r = el?.getBoundingClientRect();
  return r && r.width > 0 && r.height > 0 ? { width: r.width, height: r.height } : FALLBACK_PANE;
};

/** §1.5: a fly (a farther-afield card, a search hit) never lands wider than
 *  server zoom 8 — it zooms IN to the place, never out. */
const FLY_MIN_ENGINE_ZOOM = toEngineZoom(8);

/** A cluster expands by two levels. */
const CLUSTER_STEP = 2;

const clampZoom = (zoom: number): number =>
  Math.max(ENGINE_MIN_ZOOM, Math.min(ENGINE_MAX_ZOOM, zoom));

/** A box seen at an ENGINE zoom, as the server asks for it: normalized,
 *  because the engine's coordinates are unbounded and the server's are not —
 *  see `viewport.ts`. Raw values 400 the endpoint from five clicks of the zoom
 *  control, and the failure looks like a dead list under a working map. `null`
 *  means the camera could not say where it is looking (a container with no
 *  size reports NaN); asking nothing beats asking a question the server can
 *  only refuse. */
const viewportOf = (box: Box, engineZoom: number): Viewport | null => normalizeViewport({
  west: box.west, south: box.south, east: box.east, north: box.north,
  zoom: toServerZoom(engineZoom),
});

export const MapSurface: React.FC<{
  /** §1.8's "the filters travel": the destination page must describe the set
   *  the pin's number counted, so the chips in force at the moment of the tap
   *  go with it. Passed rather than read from the URL there, because the map's
   *  filters are the map's state — putting them in the address bar to get them
   *  across would make the surface's own control a routing concern. */
  onOpen: (slug: string, filters: ExploreFilters) => void;
  /** §1.6's miss path from the map view's own search box. Optional so callers
   *  without an AI entry point simply have no box render the Ask AI row. */
  onAskAI?: (query: string) => void;
  /** A plan row from the map view's search. */
  onOpenPlan?: (slug: string) => void;
  /** §1.3's opening frame, decided by the CALLER.
   *
   *  Fetched here, it made the map wait on a promise and turned every test of
   *  every other behaviour into an async one — eleven of them at once, which is
   *  the shape of a seam in the wrong place. The component already took its
   *  opening position from outside; the frame is the same kind of fact, just
   *  one the page has to ask for.
   *
   *  `'failed'` is distinct from a frame with no coordinates: a frame that
   *  could not be read is not evidence about the reader's home, so it opens on
   *  the default and says nothing. */
  frame?: ExploreFrame | 'failed';
  initialCentre?: [number, number];
  initialZoom?: number;
}> = ({ onOpen, onAskAI, onOpenPlan, frame,
        initialCentre = [42.0, -73.0], initialZoom = 7 }) => {
  // Map state and list state are SEPARATE. A continuation response carries
  // empty `pins` and `clusters` by design — the map has not moved, so the
  // server says nothing about it — and storing the whole response meant every
  // "Show more" erased the markers.
  const [markers, setMarkers] = useState<Pick<ExploreResponse, 'pins' | 'clusters'>>(
    { pins: [], clusters: [] });
  // Each card carries whether IT was in view, derived from its own response
  // (`position < in_view_count`, both absolute within a snapshot). A single
  // count plus a slice was wrong the moment a re-anchor merged two snapshots:
  // the divider is a property of each card, not an index into an array that
  // can change length underneath it.
  const [cards, setCards] = useState<PlacedCard[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  // 'anchor' is its own kind because its recovery differs: the cursor is gone
  // by then, so routing it to `loadMore` hits a `!cursor` guard and does
  // nothing at all — a retry button that silently no-ops.
  const [error, setError] = useState<'refresh' | 'more' | 'anchor' | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  // A REF as well as the state. `loadingMore` is read from a closure that has
  // not re-rendered yet, and `disabled` is applied on the same render, so two
  // clicks in one tick both pass the guard and both go out — against a
  // rate-limited endpoint.
  const loadingMoreRef = useRef(false);
  // §1.2's interaction is pin -> CARD -> page, not pin -> page. Navigating
  // straight off a pin skips the card, which is where the hook, the person and
  // the reason live — the reader would leave the map without ever seeing why
  // this place was worth a pin.
  const [selected, setSelected] = useState<string | null>(null);
  const [filters, setFilters] = useState<ExploreFilters>({});
  /** §1.1's narrow shape: "a list sheet raised from the bottom, and a floating
   *  List / Map toggle". Which one the reader is looking at on a narrow screen.
   *
   *  Opens on the LIST. §3 says a screen reader opens list-first, and the same
   *  reasoning serves everyone: the list is the surface that carries the
   *  content, and a reader who arrived from a link should see places, not an
   *  unlabelled map they have to interpret.
   *
   *  Ignored entirely at desktop width, where §1.1 asks for the split instead
   *  and both panels are always visible. */
  const [narrowView, setNarrowView] = useState<'list' | 'map' | 'peek'>('list');
  /** Whether the narrow layout is the one on screen.
   *
   *  Defaults to TRUE when nothing can answer, because that is what this
   *  surface did before the flag existed — the narrow states were always
   *  live in the markup and the stylesheet decided whether they meant
   *  anything. Assuming wide would instead turn the flag into a silent way to
   *  lose the peek. */
  const [isNarrow, setIsNarrow] = useState<boolean>(() => {
    const query = narrowQuery();
    return query ? query.matches : true;
  });
  /** THE TAPPED ROW THE LIST HAS NOT REACHED (slice C).
   *
   *  Held apart from `cards`, because the list's pages are the snapshot's own
   *  order: merged in, this row would take a position the snapshot did not
   *  give it, and `merge` dedupes by slug — so it would KEEP that wrong
   *  position when the page carrying it finally arrived. */
  const [fetchedCard, setFetchedCard] = useState<PlacedCard | null>(null);
  /** The slug being fetched, which is what the placeholder row is for: the
   *  peek has to say something between the tap and the card. */
  const [fetchingSlug, setFetchingSlug] = useState<string | null>(null);
  // Held in a ref TOO. `request` and `loadMore` must read the filters in force
  // at the moment they fire, and making them depend on the state instead would
  // recreate `load` on every filter change — which restarts the debounce timer
  // and, worse, makes the identity of these callbacks part of the fetching
  // logic rather than incidental to it.
  const activeFilters = useRef<ExploreFilters>({});
  const pending = useRef<number | undefined>(undefined);
  const latest = useRef(0);
  const lastViewport = useRef<Viewport | null>(null);
  // The engine's map, once it exists; `null` before, after, and in mapless mode.
  const mapRef = useRef<MapLibre.Map | null>(null);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const [engine, setEngine] = useState<{ ns: MapLibreNamespace; map: MapLibre.Map } | null>(null);
  // D14: the engine could not start (no WebGL2, or the module did not load).
  const [mapless, setMapless] = useState(false);
  // D14's logical camera: where the map WOULD be looking, kept in mapless mode
  // by the engine's own arithmetic (`mercator.ts`) so the list still moves.
  const logical = useRef<{ centre: LatLng; zoom: number } | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  /** The cursor and the narrow view as they are WHEN A PIN IS CLICKED, not as
   *  they were when the handler was memoised. Refs rather than dependencies,
   *  so a fetch started by one click cannot be re-created by the response
   *  that lands halfway through it. Written during render on purpose (the
   *  latest-ref pattern): the write is idempotent and its readers are event
   *  handlers, which never run inside a discarded render. */
  const cursorRef = useRef<string | null>(null);
  const narrowViewRef = useRef<'list' | 'map' | 'peek'>('list');
  /** WHICH TAP THIS IS — a second monotonic counter, and it is not `latest`.
   *
   *  `latest` is the question the LIST is answering; it advances on a pan or a
   *  filter change and must not advance on a pin click, or a "Show more"
   *  already in flight would be orphaned by the reader touching a pin. But two
   *  clicks in one viewport share one `latest`, so the first click's card
   *  could still land on top of the second's selection — and its rejection
   *  could navigate away from it (internal round 1, blocking 1; the #175 trap
   *  of guarding an async result with less than its session's full identity).
   *
   *  Every click takes a ticket, loaded or not — loaded too, because a loaded
   *  click is equally a decision that retires the fetch before it. */
  const tapTicket = useRef(0);
  cursorRef.current = cursor;
  narrowViewRef.current = narrowView;

  const request = useCallback((viewport: Viewport) => {
    const ticket = ++latest.current;
    fetchExplore(viewport, activeFilters.current)
      .then((response) => {
        // Out-of-order responses are dropped. A slow request for a viewport the
        // reader has already left would otherwise repaint with somewhere they
        // are no longer looking.
        if (ticket !== latest.current) return;
        setMarkers({ pins: response.pins, clusters: response.clusters });
        // A refresh REPLACES. It answers a different question — a new
        // viewport — and §1.5 is explicit that a cursor never serves results
        // for a viewport it was not minted with.
        setCards(placed(response));
        setCursor(response.next_cursor);
        setError(null);
      })
      .catch(() => { if (ticket === latest.current) setError('refresh'); });
  }, []);

  const load = useCallback((viewport: Viewport) => {
    lastViewport.current = viewport;
    // A PAN INVALIDATES THE CURSOR (§1.5: "a pan, a filter change or a profile
    // edit invalidates the cursor and starts a fresh first page — a cursor
    // never serves results for filters, a viewport or a person it was not
    // minted with"). Carrying it forward let the list keep continuing viewport
    // A while the map plainly showed B, with nothing on screen saying so.
    // The cards stay until the refresh lands — the list outlives the viewport
    // without moving it — but "Show more" cannot outlive the question.
    //
    // ORPHANING THE IN-FLIGHT RESPONSES IS PART OF THAT, and nulling the cursor
    // alone was not enough. The ticket only advanced when the debounced
    // `request` fired, 300ms later, so a continuation already in flight still
    // matched on resolve — and its `.then` set the cursor again, resurrecting
    // the button this line had just removed and merging viewport A's cards
    // under a map showing B. The question changes HERE, so the ticket does too.
    latest.current += 1;
    setCursor(null);
    // AND THE FETCHED ROW (slice C), by the same rule and in the same breath.
    // A card fetched for a pin in the old viewport answers the old question;
    // left standing it would latch in the peek after the reader had moved on,
    // which is what orphaning an in-flight page prevents for the list. The
    // ticket above also drops the fetch still in flight.
    setFetchingSlug(null);
    setFetchedCard(null);
    window.clearTimeout(pending.current);
    // Debounced: a drag fires `moveend` once but a pinch-zoom fires several,
    // and each is a request against a rate-limited endpoint.
    pending.current = window.setTimeout(() => request(viewport), DEBOUNCE_MS);
  }, [request]);

  useEffect(() => () => {
    window.clearTimeout(pending.current);
    // AND RETIRE EVERY OUTSTANDING REQUEST (round 3, B3). The guarded
    // completions compare against `latest`, so advancing it here makes each of
    // them return at its own guard — the card FAILURE branch included, whose
    // fallback is a navigation. Without this, a reader who taps an unloaded
    // pin and leaves Explore before it answers is sent to that destination
    // page when the request finally rejects, by a surface that no longer
    // exists. Not a new idea: the mobile surface has advanced its ticket on
    // unmount since r668 (`mobile/src/components/explore/ExploreSurface.tsx`),
    // which is why the same finding does not apply there.
    latest.current += 1;
  }, []);

  /** Follow the breakpoint, and RESET at it.
   *
   *  Both halves, because they answer different failures. The flag is what
   *  stops the peek filtering a list nobody asked to be filtered; the reset is
   *  what stops `narrowView` sitting on a value the reader has no control to
   *  change, since the toggle that would change it is `display: none` above
   *  the breakpoint. Either alone leaves the surface in a state the reader
   *  cannot get out of, one of them visibly (internal round 1, blocking 2).
   */
  useEffect(() => {
    const query = narrowQuery();
    if (!query) return undefined;
    const follow = (event: { matches: boolean }) => {
      setIsNarrow(event.matches);
      // Back to the state the desktop split is specified in (§1.1), so a
      // rotation back to narrow finds the list rather than a stale peek.
      if (!event.matches) setNarrowView('list');
    };
    // `addListener` is the deprecated spelling, kept as a fallback because
    // Safari gained `addEventListener` on MediaQueryList only in 14.
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', follow);
      return () => query.removeEventListener('change', follow);
    }
    if (typeof query.addListener === 'function') {
      query.addListener(follow);
      return () => query.removeListener(follow);
    }
    return undefined;
  }, []);

  /** §1.5's re-anchor: a fresh first page MERGED with what is already shown.
   *  It also refreshes the markers, because a new snapshot is a new answer
   *  about the same viewport. */
  const reanchor = useCallback((viewport: Viewport) => {
    const ticket = ++latest.current;
    // THE PLACEHOLDER GOES WHEREVER THE TICKET ADVANCES (round 3, B1; found
    // independently by both review tiers). `load` and `changeFilters` clear it
    // beside their own bump; this path advanced the ticket and did not — so a
    // card fetch already in flight came back to a guard that correctly
    // returned early, and left "Finding this place…" standing over nothing,
    // with no fallback navigation, until the reader's next tap, pan or filter
    // change. A ticket bump is the moment the placeholder's promise stops
    // being keepable, so it is the moment the placeholder goes.
    setFetchingSlug(null);
    // THE LANDED CARD STAYS, and that is the spec rather than an oversight.
    // §1.5: on expiry "the client re-anchors from a fresh first page, deduping
    // by slug across the seam" (`SPEC_EXCERPT_v3.9.md:260-262`) — a MERGE, not
    // a discard, and `setCards((current) => merge(...))` below is that same
    // rule for the list's own rows. Invalidation belongs to a CHANGE OF
    // QUESTION — a pan, a filter change or a profile edit (:819-827) — which
    // is why `load` and `changeFilters` DO clear the card and this does not.
    // An expiry is not a change of question: the reader is still asking the
    // same one. Dropping the single row they explicitly tapped while every
    // other row survived the same merge is the inconsistency. External r811
    // asked for the card to be cleared here; the spec decides against it, and
    // §10 of this slice's BUILD.md records the disagreement and the lines.
    fetchExplore(viewport, activeFilters.current)
      .then((response) => {
        if (ticket !== latest.current) return;
        setMarkers({ pins: response.pins, clusters: response.clusters });
        setCards((current) => merge(current, placed(response)));
        setCursor(response.next_cursor);
        setError(null);
      })
      .catch(() => { if (ticket === latest.current) setError('anchor'); });
  }, []);

  /** §1.5's continuation. Without it the list stops at one page and, worse, the
   *  "farther afield" divider disappears exactly when `in_view_count` fills the
   *  page — so truncation hid its own evidence. */
  const loadMore = useCallback(() => {
    const viewport = lastViewport.current;
    if (!cursor || !viewport || loadingMoreRef.current) return;
    // The continuation carries the SAME ticket discipline as a refresh: without
    // it, a slow page could append rows belonging to a viewport the reader has
    // already left, under a list showing somewhere else.
    const ticket = latest.current;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    fetchExplore(viewport, activeFilters.current, cursor)
      .then((response) => {
        if (ticket !== latest.current) return;
        // ONLY list state. The map is untouched, because a continuation says
        // nothing about it.
        setCards((current) => merge(current, placed(response)));
        setCursor(response.next_cursor);
        setError(null);
      })
      .catch((err: { status?: number; expired?: boolean }) => {
        if (ticket !== latest.current) return;
        // §1.5's expiry contract: a 409 means the snapshot is gone and the
        // client re-anchors from a fresh first page, deduping by slug across
        // the seam — which the append above already does. Anything else is a
        // failure the reader can retry.
        // §1.5: "On expiry the endpoint says so and the client re-anchors from
        // a fresh first page, deduping by slug across the seam." DEDUPING is
        // the word that matters — it only means anything if the fresh page is
        // merged with what the reader already has. Calling the ordinary
        // refresh here threw away every page they had scrolled through, which
        // is the loss the seam language exists to prevent.
        // A 400 lands here too: it means the cursor does not describe this
        // request, and re-anchoring TERMINATES where retrying the rejected
        // pair does not.
        if (err?.status === 409 || err?.status === 400) {
          setCursor(null);
          reanchor(viewport);
          return;
        }
        setError('more');
      })
      .finally(() => { loadingMoreRef.current = false; setLoadingMore(false); });
  }, [cursor, reanchor]);

  // Retries what actually failed. A single retry that always refreshed the
  // first page discarded every accumulated continuation page — recovering from
  // a failed "show more" by throwing away what the reader had already scrolled.
  const retry = useCallback(() => {
    if (!lastViewport.current) return;
    if (error === 'anchor') reanchor(lastViewport.current);
    else if (error === 'more') loadMore();
    else request(lastViewport.current);
  }, [error, loadMore, reanchor, request]);

  /** §1.5 names a filter change alongside a pan: "a pan, a filter change or a
   *  profile edit invalidates the cursor and starts a fresh first page — a
   *  cursor never serves results for filters, a viewport or a person it was not
   *  minted with." So this does exactly what `load` does, for the same reason,
   *  including advancing the ticket: a continuation already in flight was
   *  minted under the OLD filters and would otherwise resolve and append rows
   *  that do not match the chips now showing. */
  const changeFilters = useCallback((next: ExploreFilters) => {
    activeFilters.current = next;
    setFilters(next);
    // Bumping here is redundant while `request` bumps synchronously below —
    // kept because it is what makes the invalidation true of `changeFilters`
    // itself rather than of something it happens to call, and because it
    // becomes load-bearing the moment this grows a delay.
    latest.current += 1;
    setCursor(null);
    // The pan's pending refresh is CANCELLED. It would fire with the same
    // viewport and, through the ref, the same filters — a second identical
    // request, and one that can share a ticket with a continuation, since
    // `loadMore` captures the ticket rather than advancing it.
    window.clearTimeout(pending.current);
    // THE ROWS AND PINS GO TOO, and this is where a filter change parts company
    // with a pan. §1.5 keeps the list through a pan because those cards are
    // still true — the places exist, they are merely out of frame. After a
    // filter change they may not match at all, so holding them under the new
    // chips presents them as matches, which is exactly what §1.8's "narrow what
    // is in view and say so" forbids. Worse, a failed request left them there
    // permanently. Clearing costs one round trip of empty and cannot lie.
    setMarkers({ pins: [], clusters: [] });
    setCards([]);
    // The fetched row goes with the rest: it was built under the old chips and
    // may not match the new ones at all.
    setFetchingSlug(null);
    setFetchedCard(null);
    // Not debounced: the pan debounce exists to collapse the several mechanical
    // `moveend`/`zoomend` events of ONE gesture, not to merge separate
    // intentions. (Not quite "one deliberate press", either — arrow keys on a
    // radio group select as they move, so traversing a dimension can fire one
    // request per option. Bounded by the option count and negligible against
    // `explore_map`'s 1200/hour.)
    if (lastViewport.current) request(lastViewport.current);
  }, [request]);

  /** Mapless mode's camera command (D14): move the logical camera and ask
   *  for what it would show, by the same `load` a `moveend` would reach. */
  const moveLogical = useCallback((centre: LatLng, zoom: number) => {
    const z = clampZoom(zoom);
    logical.current = { centre, zoom: z };
    const viewport = viewportOf(boundsForCamera(centre, z, paneSizeOf(canvasRef.current)), z);
    if (viewport) load(viewport);
  }, [load]);

  /** A cluster expands two levels in. With no map and no logical camera yet
   *  (the engine still loading) there is nothing to move, and nothing is
   *  attempted. */
  const zoomTo = useCallback((lat: number, lng: number) => {
    const map = mapRef.current;
    if (map) {
      map.easeTo({ center: [lng, lat], zoom: map.getZoom() + CLUSTER_STEP });
    } else if (logical.current) {
      moveLogical([lat, lng], logical.current.zoom + CLUSTER_STEP);
    }
  }, [moveLogical]);

  /** §1.5: "Scrolling never moves the map; tapping a farther-afield card flies
   *  it." Flying is what makes the card reachable — it is outside the frame, so
   *  navigating straight to its page skips the map's own answer to "where is
   *  this?". */
  const flyTo = useCallback((lat: number, lng: number) => {
    const map = mapRef.current;
    if (map) {
      map.flyTo({ center: [lng, lat], zoom: Math.max(map.getZoom(), FLY_MIN_ENGINE_ZOOM) });
    } else if (logical.current) {
      moveLogical([lat, lng], Math.max(logical.current.zoom, FLY_MIN_ENGINE_ZOOM));
    }
  }, [moveLogical]);

  /** The card for a pin the list has not reached, from the reader's OWN
   *  snapshot (slice C, decision 6 in `docs/EXPLORE_SHEET_LAYOUT.html`).
   *
   *  Through the server rather than off the pin: a card is a photograph and
   *  its credit, a hook, a trip line and a reason chosen against the row above
   *  it, and duplicating that on a pin duplicates the copy rules with it,
   *  which is how two renderings of one place drift. The cursor travels
   *  because the reason line only agrees with the list's if the server can see
   *  WHICH list is being asked about.
   *
   *  With no cursor in hand there is nothing to ask about, and nothing to ask
   *  ABOUT: a list with no continuation is entirely loaded, so every pin in it
   *  has a card. That leaves the re-anchor's brief gap, and there the
   *  destination page is the honest answer rather than a wait. Every failure
   *  falls back the same way — which was this surface's whole behaviour for an
   *  unloaded pin before this slice. */
  const fetchOneCard = useCallback((slug: string, tap: number) => {
    const viewport = lastViewport.current;
    const held = cursorRef.current;
    if (!viewport || !held) {
      onOpen(slug, activeFilters.current);
      return;
    }
    const ticket = latest.current;
    const wasMap = narrowViewRef.current === 'map';
    setFetchedCard(null);
    setFetchingSlug(slug);
    if (wasMap) setNarrowView('peek');
    fetchExploreCard(viewport, activeFilters.current, held, slug)
      .then((response) => {
        // TWO tickets, because there are two questions. `latest` is the
        // list's: a card that arrives after a pan or a filter change answers
        // a question the reader stopped asking. `tapTicket` is this click's: a
        // card that arrives after the reader clicked something ELSE would land
        // on top of their later choice, in the same viewport, with `latest`
        // unmoved (internal round 1, blocking 1).
        if (ticket !== latest.current || tap !== tapTicket.current) return;
        setFetchingSlug(null);
        setFetchedCard({
          ...response.card,
          inView: response.card.position < response.in_view_count,
        });
        setSelected(slug);
      })
      .catch(() => {
        // Guarded on BOTH, and this half matters more: a stale failure would
        // not merely fail to paint, it would navigate away from the pin the
        // reader has since chosen.
        if (ticket !== latest.current || tap !== tapTicket.current) return;
        setFetchingSlug(null);
        if (wasMap) setNarrowView('map');
        onOpen(slug, activeFilters.current);
      });
  }, [onOpen]);

  const choose = useCallback((slug: string) => {
    // EVERY click, loaded or not: a click is the reader choosing, and it
    // retires whatever the click before it was still waiting for.
    const tap = ++tapTicket.current;
    // The pin list is unbounded by design but the card list is paged, so a pin
    // whose card sits past the loaded pages has nothing to select. Slice C
    // fetches that one row rather than leaving the map's promise kept for the
    // cards that happen to be loaded and broken for the rest.
    //
    // A pin whose card was FETCHED a moment ago is loaded — it is on screen,
    // in the peek. Asking again would spend a request rebuilding the row
    // already in front of the reader, behind a placeholder flicker.
    if (!cards.some((c) => c.slug === slug) && fetchedCard?.slug !== slug) {
      fetchOneCard(slug, tap);
      return;
    }
    setSelected(slug);
    // The placeholder belonged to the tap this one retires. The ticket keeps
    // that fetch's RESULT out; the row it already put on screen has to be
    // taken out here, or "Finding this place…" sits above the card the reader
    // has just chosen and never leaves.
    setFetchingSlug(null);
    // §1.2: "Tap a pin → the sheet shows that destination's card." On the
    // narrow map view the sheet is LOWERED, so selecting alone marked a card
    // nobody could see and the scroll effect scrolled an invisible sheet —
    // the tap did nothing a reader could perceive. It used to raise the whole
    // list for that; slice C raises the sheet only far enough to hold the row
    // the reader tapped, so the map they are working in stays in front of
    // them. A reader already ON the list is left alone: their sheet is up,
    // the row is in it, and shrinking it to one row would be taking the list
    // away from someone who is reading it.
    if (narrowViewRef.current === 'map') setNarrowView('peek');
  }, [cards, fetchedCard, fetchOneCard]);

  // The scroll is an EFFECT, not a line after `setSelected`. Querying for the
  // selected card in the handler reads the DOM before React has re-rendered it,
  // so the node is not there yet — the first version happened to work only if a
  // rAF landed after the flush, which is an ordering to depend on rather than a
  // guarantee. A selection nobody can perceive is not a selection: on the narrow
  // layout the list sits ABOVE the map, so the chosen card is off-screen at the
  // moment of the tap by construction.
  useEffect(() => {
    if (!selected) return;
    listRef.current
      ?.querySelector('.explore-card--selected')
      ?.scrollIntoView({ block: 'nearest' });
    // `cards` is a dependency on purpose: a search hit selects a destination
    // whose card has not arrived yet — the fly moves the viewport and the card
    // lands a refresh later. Keyed on the selection alone, the scroll fired
    // into a list that did not hold the card, and never fired again.
  }, [selected, cards]);

  // The pins and clusters, as engine markers. Declared BEFORE the map's own
  // effect on purpose: on unmount React runs the cleanups in declaration
  // order, so the markers come off a live map before it is removed — the
  // engine forbids any use of a removed map. The handlers go through refs so a
  // new `choose` (it changes with the selection) does not rebuild every marker.
  const chooseRef = useRef(choose);
  chooseRef.current = choose;
  const zoomToRef = useRef(zoomTo);
  zoomToRef.current = zoomTo;
  useEffect(() => {
    if (!engine) return undefined;
    const { ns, map } = engine;
    const place = (element: HTMLElement, lat: number, lng: number, activate: () => void) => {
      // `click` only. The element is a native `<button>`, which the browser
      // already activates on Enter and Space by dispatching `click`; a second
      // keyboard path would choose twice for one key press.
      element.addEventListener('click', activate);
      return new ns.Marker({ element, anchor: 'center' }).setLngLat([lng, lat]).addTo(map);
    };
    // The pins, which a first version fetched and never drew — the map
    // rendered tiles alone while `Pin` and `Cluster` were imported by nothing
    // but their own passing tests.
    const placed = [
      ...markers.pins.map((pin) => place(
        pinElement(pin), pin.lat, pin.lng, () => chooseRef.current(pin.slug))),
      // A cluster expands. Without a handler it was focusable and inert — a
      // control that looks like one and does nothing.
      ...markers.clusters.map((cluster) => place(
        clusterElement(cluster), cluster.lat, cluster.lng,
        () => zoomToRef.current(cluster.lat, cluster.lng))),
    ];
    return () => { placed.forEach((marker) => marker.remove()); };
  }, [engine, markers]);

  // THE MAP ITSELF, once the frame has settled (below: nothing mounts before).
  //
  // Created once and never remounted (Almanac decision 10: one basemap, so
  // nothing disturbs the §1.5 cursor mid-pan). Everything it opens with is
  // read when the frame settles, through a ref: a later change to the props
  // must not drag the map back from wherever the reader has panned it.
  const frameSettled = frame !== undefined;
  const openingRef = useRef({ frame, initialCentre, initialZoom });
  openingRef.current = { frame, initialCentre, initialZoom };
  useEffect(() => {
    if (!frameSettled) return undefined;
    const settled = openingRef.current;
    const framed = framedOf(settled.frame);
    const opening: LatLng = framed
      ? [framed.lat as number, framed.lng as number] : settled.initialCentre;
    // §1.3's radius as a box, the same region at every container width: a
    // zoom number would have to be guessed from the width, so "250 km" would
    // mean a different amount of world on a phone and in the split view.
    const fitBox = framed && framed.radius_km > 0 ? radiusBounds(opening, framed.radius_km) : null;
    let alive = true;
    let map: MapLibre.Map | null = null;
    let credit: HTMLElement | null = null;
    let observer: ResizeObserver | null = null;

    // D10: every report is the camera's own state, read at `moveend` — which
    // the engine fires after zooms too, and which belongs to the transform,
    // not the style. NOTHING here waits on `load`, `style.load`, `idle` or
    // `styledata`: a style that fails to arrive is a blank canvas over a
    // working list, as Leaflet's missing tiles were, never an empty list.
    const report = () => {
      if (!map) return;
      const b = map.getBounds();
      const viewport = viewportOf(
        { west: b.getWest(), south: b.getSouth(), east: b.getEast(), north: b.getNorth() },
        map.getZoom());
      if (viewport) load(viewport);
    };

    // D14: no engine. The pane says so, stays laid out, and the list opens on
    // the viewport the engine would have fitted — the same arithmetic over the
    // pane's measured size. ONE warning names which way the engine failed, so
    // a 404 on the versioned engine URL reads differently from a browser
    // without WebGL; the error itself rides along for whoever is looking.
    const enterMapless = (reason: 'no-webgl' | 'engine-unavailable', error: unknown) => {
      console.warn(`Explore map unavailable (${reason}); showing the list only.`, error);
      setMapless(true);
      const size = paneSizeOf(canvasRef.current);
      const camera = fitBox
        ? cameraForBounds(fitBox, size, { minZoom: ENGINE_MIN_ZOOM, maxZoom: ENGINE_MAX_ZOOM })
        : { centre: opening, zoom: toEngineZoom(settled.initialZoom) };
      moveLogical(camera.centre, camera.zoom);
    };

    loadEngine().then((ns) => {
      const container = canvasRef.current;
      if (!alive || !container) return;
      try {
        map = new ns.Map({
          container,
          style: STYLE_URLS.base,
          transformRequest: cartoTransformRequest(),
          attributionControl: false,
          center: [opening[1], opening[0]],
          zoom: toEngineZoom(settled.initialZoom),
          maxZoom: ENGINE_MAX_ZOOM,
          minZoom: ENGINE_MIN_ZOOM,
          // Rotation and pitch stay off (plan §1 non-goal, and the bearing-0
          // camera arithmetic of D9).
          dragRotate: false,
          pitchWithRotate: false,
          touchPitch: false,
        });
      } catch (error) {
        // Only the constructor is caught: 6.x throws here without WebGL2.
        // Anything later is a defect and is left to surface.
        enterMapless('no-webgl', error);
        return;
      }
      const live = map;
      live.addControl(new ns.NavigationControl({ showCompass: false, showZoom: true }), 'top-left');
      live.touchZoomRotate.disableRotation();
      live.keyboard.disableRotation();
      credit = container.appendChild(creditElement());
      live.on('moveend', report);
      mapRef.current = live;
      setEngine({ ns, map: live });

      // Report again when the CONTAINER changes size. This surface moves
      // between three branches of its page, each with a different layout, and
      // the engine re-measures on window resize only — so it is told EVERY
      // time, or it draws a viewport it no longer occupies. But the SERVER is
      // asked only when the box moved enough to be a different question:
      // clearing the list on a filter change shortens the page, its scrollbar
      // disappears, the map widens by ~15px, and without the threshold a second
      // request goes out for a viewport nobody moved (measured in a browser as
      // three requests for one chip press). Seeded from the box AS IT IS, not
      // from zero, so the first resize gets no free pass through it.
      if (typeof ResizeObserver !== 'undefined') {
        const start = container.getBoundingClientRect();
        let last = { w: start.width, h: start.height };
        observer = new ResizeObserver(() => {
          live.resize();
          const { width: w, height: h } = container.getBoundingClientRect();
          if (Math.abs(w - last.w) < 24 && Math.abs(h - last.h) < 24) return;
          last = { w, h };
          report();
        });
        observer.observe(container);
      }

      if (fitBox) {
        // The fit's `moveend` is the first report (D10). Not animated: the
        // reader has not seen the map anywhere else yet.
        live.fitBounds([[fitBox.west, fitBox.south], [fitBox.east, fitBox.north]], { animate: false });
      } else {
        // No frame to fit: report the camera as constructed, or a reader who
        // never touches the map would see an empty list forever.
        report();
      }
    }, (error: unknown) => {
      // The engine module did not load (a 404 on the versioned URL, or no
      // dynamic import): the same mapless mode as a constructor without WebGL.
      if (alive) enterMapless('engine-unavailable', error);
    });

    return () => {
      alive = false;
      if (observer) observer.disconnect();
      if (map) map.remove();
      if (credit) credit.remove();
      mapRef.current = null;
      logical.current = null;
      setEngine(null);
    };
  }, [frameSettled, load, moveLogical]);

  // THE MAP DOES NOT MOUNT UNTIL THE FRAME HAS SETTLED.
  //
  // Rendering immediately and fitting when the frame lands looked harmless and
  // was not: every open mounted at the hardcoded default this slice exists to
  // replace, fetched the whole list for that viewport, and then jumped. And
  // `fitted` only guards REPEAT fits — a reader who had already panned during a
  // slow frame request got the map yanked out from under them, which is the
  // motion §1.5 forbids in as many words.
  //
  // Waiting costs one cached GET. The earlier objection to waiting was about
  // fetching INSIDE this component, which made every unrelated test async; the
  // caller owning the fetch is what makes waiting cheap. Nothing interactive
  // renders meanwhile, deliberately: chips with no viewport behind them are
  // controls that look like controls and do nothing.
  if (frame === undefined) {
    return (
      <div className="explore-surface">
        <p className="explore-surface__status" role="status">Finding your map…</p>
      </div>
    );
  }

  // Shown only when the frame is REAL and not built from a home. An unread
  // frame says nothing about whether the reader has one — but the flag is
  // read from the RESOLVED frame, not the coordinate-bearing `framed`
  // (mobile r532, ported back): an unseeded atlas returns null coordinates
  // WITH `home_prompt: true` on purpose, and reading through `framed`
  // suppressed the invitation exactly where it helps most. A FAILED frame
  // stays silent: it said nothing about a home.
  const resolvedFrame = frame && frame !== 'failed' ? frame : null;
  const showHomePrompt = Boolean(resolvedFrame?.home_prompt);

  /** The fetched row, for as long as the list itself has not reached it.
   *  §1.5's "deduping by slug", one row wide: the moment a page brings the
   *  slug in, the fetched copy goes and the row is the list's own — at the
   *  position the snapshot gave it, which is where it would have been had the
   *  reader simply scrolled. */
  const fetchedRow = fetchedCard && fetchedCard.slug === selected
    && !cards.some((c) => c.slug === fetchedCard.slug) ? fetchedCard : null;
  /** The fetched row LEADS the list, because a peek shows the top of it: a
   *  row at position 34 would be a selection the reader has to go looking
   *  for, which is the interaction this slice exists to remove. */
  const listCards = fetchedRow ? [fetchedRow, ...cards] : cards;
  /** IN THE PEEK THE SHEET HOLDS THE TAPPED ROW ALONE.
   *
   *  Which is also why the peek needs no height of its own: the sheet is
   *  sized by its content under the existing 72% cap, so one row is one row's
   *  worth of sheet and the map keeps the rest. Choosing a percentage here
   *  would be inventing a number the layout does not need, and the reader
   *  would then have rows to scroll past to see the one they tapped. */
  const inPeek = isNarrow && narrowView === 'peek';
  const shownCards = inPeek
    ? listCards.filter((c) => c.slug === selected) : listCards;
  /** A peek with nothing in it is the map view. It happens by an ordinary
   *  route — the reader pans, the refresh replaces the list, and the row they
   *  had tapped is no longer in it — and a raised sheet holding no row is a
   *  band of empty white over the map they moved to. Derived rather than
   *  stored, so nothing has to notice the moment it becomes true. */
  const panelView = inPeek && !fetchingSlug && shownCards.length === 0
    ? 'map' : narrowView;

  return (
    <div className="explore-surface">
      {/* List first in the DOM AND first visually (§1.1). */}
      {/* §1.8 puts the chip row "over the map", and it governs the list as
          much as the markers — so it spans the surface rather than sitting in
          either column. Placing it inside the list column made it read as a
          list control, which is half of what it is. It stays FIRST in the DOM
          either way: a control belongs before the thing it controls. */}
      <ViewFilters filters={filters} onChange={changeFilters} />
      {/* OUTSIDE the sheet, because the sheet is lowered in map view with
          `visibility: hidden` — which removes it from the accessibility tree —
          and a refresh failure while looking at the map was announced to no
          one. The failure belongs to the surface, not to one of its panels. */}
      <p role="status" className="explore-surface__status">
        {error === 'refresh' ? 'Could not refresh this list.'
          : error ? 'Could not load more places.' : ''}
      </p>
      {error && (
        <button type="button" className="explore-surface__retry" onClick={retry}>
          Try again
        </button>
      )}
      <div className={`explore-surface__panels explore-surface__panels--${panelView}`}>
      <div className="explore-surface__list" ref={listRef}>
        {/* A ROW IN THE SHEET, not a spinner over the map (slice C): the map
            is the surface the reader just clicked, and covering it to say
            "loading" hides the one thing they are looking at. `role=status`
            because a reader who cannot see the sheet rise is owed the same
            news by another route. */}
        {fetchingSlug && (
          <p className="explore-list__pending" role="status">
            Finding this place…
          </p>
        )}
        <ListSheet
          cards={shownCards}
          onOpen={(slug) => onOpen(slug, activeFilters.current)}
          onFly={flyTo}
          selectedSlug={selected}
        />
        {/* Not in the peek: the continuation belongs to the LIST, and a peek
            holding one row plus "Show more places" would offer to lengthen a
            list it is not showing. */}
        {cursor && !inPeek && (
          <button
            type="button"
            className="explore-surface__more"
            onClick={loadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading…' : 'Show more places'}
          </button>
        )}
      </div>
      <div className="explore-surface__map">
        {/* The engine's container: React renders it empty and never renders
            into it — the engine owns its children (canvas, controls, the
            pins as marker buttons, the credit). One basemap (Almanac decision
            10), greyed by ExploreSurface.css on the canvas alone. */}
        <div ref={canvasRef} className="explore-surface__canvas" data-testid="explore-map-canvas" />
        {/* D14: the pane stays laid out (never `display:none`) and says why it
            is blank; the list below it is still the whole answer. */}
        {mapless && (
          <p className="explore-surface__status explore-surface__mapless" role="status">
            This browser can&apos;t draw the map; the list shows places near where it would be looking.
          </p>
        )}

        {/* §1.3: "the map shows 'Set your home to see what's an hour away'
            WHERE 'YOU' WOULD BE" — the centre of a frame that is not built from
            a home is exactly the spot a home would have put the reader, so the
            invitation sits there rather than in a corner.
            A link, not a button: it goes to the page that already holds the
            home field, and a reader should be able to open it in a new tab or
            see where it leads before pressing. */}
        {showHomePrompt && (
          <Link to="/settings" className="explore-home-prompt">
            <span className="explore-home-prompt__spot" aria-hidden="true" />
            <span className="explore-home-prompt__label">
              Set your home to see what’s an hour away
            </span>
          </Link>
        )}

      {/* §1.1's "search over it", on the MAP VIEW and on the PEEK (slice
          4c-3; the peek added by slice C, which is a map view with one row
          showing — removing the box because a pin was tapped would be taking
          a control away for using another). The list view keeps the inline
          box (§1.6: "the inline atlas-first results stay for the list view"),
          and the desktop split never sets `narrowView` to 'map' or 'peek' —
          the toggle that would is hidden there and the breakpoint listener
          resets it — so this renders on exactly the surfaces it is meant to.
          Round 528: this box was `position: fixed`, owned by the VIEWPORT —
          scrolling down to the page content below left it floating over the
          inline HeroSearch, two live search boxes stacked on one screen. It
          now lives INSIDE the map pane (absolute, in the map's isolated
          stack), so it covers exactly the surface it searches and scrolls
          away with it.
          An atlas hit FLIES the map and opens its card, which is the pin-tap
          interaction arrived at by search; a hit with no coordinates opens its
          page instead, because flying needs somewhere to fly to — the same
          fallback as the farther-afield card. An UNPINNED hit (no current
          edition, no published plan) also opens its page: the map would draw
          no pin there and the sheet would raise with no card for it, a promise
          the fly cannot keep — where the destination page renders for any
          atlas row. Residual, recorded in the slicing doc: a pinned hit that
          the ACTIVE FILTERS exclude still flies to a sheet without its card;
          clearing the reader's filters behind their back would be worse. */}
      {(narrowView === 'map' || narrowView === 'peek') && (
        <MapSearch
          onHit={(slug, lat, lng, pinned) => {
            if (lat === null || lng === null || !pinned) {
              onOpen(slug, activeFilters.current);
              return;
            }
            // NOT `choose`: that helper opens the PAGE when the card is not
            // among the loaded ones — and a searched destination almost never
            // is, which is why it was searched for. The fly moves the viewport,
            // the refresh brings the destination's card in, and the latched
            // selection marks it when it lands.
            flyTo(lat, lng);
            setSelected(slug);
            setNarrowView('list');
          }}
          onPlan={(slug) => onOpenPlan?.(slug)}
          onAskAI={(q) => onAskAI?.(q)}
        />
      )}
      </div>
      </div>
      {/* §1.1's floating toggle, OUTSIDE both panels.
          Inside the map pane it was hidden exactly when it was needed — the
          list view hides that pane, so a reader could never switch to the map.
          A control belongs outside the thing it controls.
          Hidden by the stylesheet at desktop width, where §1.1 asks for the
          split and both panels are already visible. */}
      <button
        type="button"
        className="explore-toggle"
        onClick={() => setNarrowView((v) => (v === 'list' ? 'map' : 'list'))}
      >
        {narrowView === 'list' ? 'Show map' : 'Show list'}
      </button>
    </div>
  );
};
